import { useEffect, useMemo, useState, useRef } from 'react';

const DEFAULT_FIELDS = [];
const DEFAULT_FORM_DEFAULTS = {};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  fields = DEFAULT_FIELDS,
  formDefaults = DEFAULT_FORM_DEFAULTS,
  idKey,
  title = 'Editor',
  onClose,
  onSubmit,
  inline = false,

  // 👇 nuevos props opcionales
  modalMaxHeight = '85vh',
  modalWidth = 'min(980px, 96vw)',
  children,
  hideFooter = false,
  hideHeaderPrefix = false,
}) {
  const initial = useMemo(() => {
    if (mode === 'edit' && data) {
      return { ...formDefaults, ...data };
    }
    return formDefaults;
  }, [mode, data, formDefaults]);

  const [form, setForm] = useState(initial);

  const syncRef = useRef('');

  useEffect(() => {
    // Solo sincronizamos si hay campos definidos (modo formulario interno)
    if (fields && fields.length > 0) {
      const currentToken = JSON.stringify({ mode, data, open });
      if (currentToken !== syncRef.current) {
        syncRef.current = currentToken;
        setForm(initial);
      }
    }
  }, [mode, data, open, initial, fields]);

  if (!open) return null;

  const updateField = (name, value, type) => {
    let v = value;
    switch (type) {
      case 'checkbox': v = !!value; break;
      case 'number': {
        if (value === '' || value == null) v = '';
        else {
          const n = Number(value);
          v = Number.isNaN(n) ? '' : n;
        }
        break;
      }
      default: v = typeof value === 'string' ? value : (value ?? ''); break;
    }
    setForm(prev => ({ ...prev, [name]: v }));
  };

  const isValid = fields.every(f => {
    const v = form[f.name];
    if (f.required) {
      if (f.type === 'checkbox') { if (!v) return false; }
      else if (f.type === 'number') { if (v === '' || v == null) return false; }
      else { if (v == null || String(v).trim() === '') return false; }
    }
    if (f.type === 'number' && v !== '' && v != null) {
      if (typeof f.min === 'number' && v < f.min) return false;
      if (typeof f.max === 'number' && v > f.max) return false;
    }
    if (f.type === 'email' && v) {
      const simple = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!simple.test(String(v))) return false;
    }
    return true;
  });

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',              // 👈 fijo
      }}
    >
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {hideHeaderPrefix ? title : (mode === 'new' ? `Nuevo - ${title}` : `Editar - ${title}`)}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: fields.length > 0 ? '15%' : '16px',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '0.8rem',
        overflowY: 'auto',             // 👈 scroll vive aquí
        flex: '1 1 auto',              // 👈 ocupa todo el alto disponible
        minHeight: 0,                  // 👈 imprescindible para que el scroll funcione en flex
      }}
    >
      {children}
      {fields.map((f) => {
        const val = form[f.name] ?? (f.type === 'checkbox' ? false : '');
        const baseId = `fld_${f.name}`;
        const common = { id: baseId, name: f.name, className: 'infoField', placeholder: f.placeholder };

        return (
          <div key={f.name}>
            <label className="roles-title" style={{ fontSize: '.95rem' }} htmlFor={baseId}>
              {f.label}{f.required ? ' *' : ''}
            </label>
            <br />

            {f.type === 'textarea' && (
              <textarea
                {...common}
                value={val}
                maxLength={f.maxLength}
                style={{ minHeight: 100, resize: 'vertical' }}
                onChange={(e) => updateField(f.name, e.target.value, 'textarea')}
              />
            )}

            {(f.type === 'text' || f.type === 'email' || f.type === 'date' || f.type === 'datetime-local' || f.type === 'time') && (
              <input
                {...common}
                type={f.type}
                value={val}
                maxLength={f.maxLength}
                onChange={(e) => updateField(f.name, e.target.value, f.type)}
              />
            )}

            {f.type === 'number' && (
              <input
                {...common}
                type="number"
                value={val === 0 ? 0 : val}
                min={f.min}
                max={f.max}
                step={f.step}
                onChange={(e) => updateField(f.name, e.target.value, 'number')}
              />
            )}

            {f.type === 'select' && (
              <select
                {...common}
                value={val}
                onChange={(e) => updateField(f.name, e.target.value, 'select')}
              >
                {(f.options || []).map(opt => {
                  const isObj = typeof opt === 'object';
                  const key = isObj ? (opt.value ?? opt.label) : opt;
                  const value = isObj ? (opt.value ?? opt.label) : opt;
                  const label = isObj ? (opt.label ?? opt.value) : opt;
                  return <option key={key} value={value}>{label}</option>;
                })}
              </select>
            )}

            {f.type === 'checkbox' && (
              <input
                id={baseId}
                name={f.name}
                type="checkbox"
                checked={!!val}
                onChange={(e) => updateField(f.name, e.target.checked, 'checkbox')}
              />
            )}

            {f.type === 'radio' && (
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                {(f.options || []).map(opt => {
                  const isObj = typeof opt === 'object';
                  const value = isObj ? opt.value : opt;
                  const label = isObj ? opt.label : opt;
                  return (
                    <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                      <input
                        type="radio"
                        name={f.name}
                        value={value}
                        checked={form[f.name] === value}
                        onChange={() => updateField(f.name, value, 'radio')}
                      />
                      {label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',               // 👈 fijo
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => onSubmit(form, mode)}
        disabled={!isValid}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) return <div className="roles-inline-card">{Header}{Body}{Footer}</div>;

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} modal`}
      style={{
        position: 'fixed',           // por si la clase no lo tiene
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: modalWidth,
          maxHeight: modalMaxHeight, // 👈 límite de altura del modal
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',         // evita que el body empuje el panel
        }}
      >
        {Header}
        {Body}
        {!hideFooter && Footer}
      </div>
    </div>
  );
}
